import { Layout } from 'react-grid-layout';
import {
  BUBBLE_CHART_WIDGET_COL_SPAN,
  DASHBOARD_COL_NUMBER,
  DASHBOARD_GAP,
  DASHBOARD_MAX_WIDTH,
  DASHBOARD_MIN_WIDTH,
  DEFAULT_WIDGET_COL_SPAN,
  HEADER_WIDGET_COL_SPAN,
  PAGE_BODY_PADDING,
  WIDGET_ADD_BUTTON_ID,
  WIDGET_MIN_ROWS,
} from './consts';
import { sortedUniq } from 'lodash';
import { WidgetTypes } from '@ardoq/api-types';
import {
  DashboardBuilderWidget,
  DashboardBuilderWidgetsProps,
} from './widgets/types';

const SUPPORTED_WIDGETS_FOR_RESIZING: WidgetTypes[] = [
  WidgetTypes.TABLE,
  WidgetTypes.BAR_CHART,
  WidgetTypes.PIE_CHART,
  WidgetTypes.STACKED_BAR_CHART,
  WidgetTypes.NUMBER,
  WidgetTypes.LINE_CHART,
  WidgetTypes.BUBBLE_CHART,
  WidgetTypes.NOTE,
  WidgetTypes.POWER_BI_IFRAME,
];

export enum BreakpointKeys {
  XS = 'xs',
  S = 's',
  M = 'm',
  L = 'l',
  XL = 'xl',
}

export const getDashboardWidthWithGap = (dashboardWidth: number) => {
  // React grid layout library adds an outer margin to the dashboard that we have to adjust for by setting negative margin on the element.
  // That's why we need to add the gap values to the dashboard width to get the actual width.
  return dashboardWidth + 2 * DASHBOARD_GAP;
};

export const breakpointKeyList = [
  BreakpointKeys.XS,
  BreakpointKeys.S,
  BreakpointKeys.M,
  BreakpointKeys.L,
  BreakpointKeys.XL,
];

const dashboardWidthBreakpoints: Record<BreakpointKeys, number> = {
  [BreakpointKeys.XS]: getDashboardWidthWithGap(DASHBOARD_MIN_WIDTH),
  [BreakpointKeys.S]: 1320,
  [BreakpointKeys.M]: 1479,
  [BreakpointKeys.L]: 1608,
  [BreakpointKeys.XL]: DASHBOARD_MAX_WIDTH,
} as const;

export const getBreakpointFromWidth = (
  dashboardWidth: number,
  isSidebarRendered: boolean,
  pageBodyWidth: number | null
) => {
  const actualWidth =
    isSidebarRendered && pageBodyWidth
      ? pageBodyWidth - PAGE_BODY_PADDING * 2
      : dashboardWidth;
  return (
    breakpointKeyList.find(
      breakpoint => actualWidth < dashboardWidthBreakpoints[breakpoint]
    ) ?? BreakpointKeys.XL
  );
};

export const getWidgetRows = (breakpointIndex: number) =>
  breakpointIndex > 0
    ? WIDGET_MIN_ROWS + (breakpointIndex - 1)
    : WIDGET_MIN_ROWS;

const getGridLayoutLibrarySpecificYValue = (
  mappedYValues: number[],
  y: number // When a widget is deleted, there can sometimes be a "hole" in the y-values if the deleted widget was the last one on that row, calling this function recursively makes sure we get a value and don't pass y=undefined to the grid layout library
): number =>
  mappedYValues[y]
    ? mappedYValues[y]
    : y <= 0 // protect against returning undefined or negative numbers if there is no y value defined for 0
      ? 0
      : getGridLayoutLibrarySpecificYValue(mappedYValues, y - 1);

export const getLayout = (
  currentBreakpoint: BreakpointKeys,
  isDragging: boolean,
  widgets: DashboardBuilderWidgetsProps[],
  isSaving: boolean,
  isInLinkDashboardFiltersMode: boolean
): Layout[] => {
  // Based on the current width of the dashboard (currentBreakpoint) map the height and y position of the widget to make it responsive
  const widgetBaseRows = getWidgetRows(
    breakpointKeyList.indexOf(currentBreakpoint!)
  );
  const mappedYValues: number[] = []; // We add up height to y values to find the correct y value for the current breakpoint
  // If you delete the first row immediately after there is no y=0. So just use the lowest one. The grid layout library will move everything up to y 0 after
  const minY = Math.min(...widgets.map(({ layout: { y } }) => y)) ?? 0;
  const breakpointAdjustedLayout = widgets.map<Layout>(
    ({ layout: { h, y, ...layout }, viewType }) => {
      const isHeader = viewType === WidgetTypes.HEADER;
      const isPowerBiIframe = viewType === WidgetTypes.POWER_BI_IFRAME;
      const height = isHeader ? 1 : h * widgetBaseRows; // Header widgets span always just 1 row
      if (y === minY && !mappedYValues[y]) {
        mappedYValues[y] = height;
      } else if (!mappedYValues[y]) {
        mappedYValues[y] = mappedYValues[mappedYValues.length - 1] + height;
      }
      return {
        ...layout,
        h: height,
        y:
          y > minY
            ? getGridLayoutLibrarySpecificYValue(mappedYValues, y - 1)
            : 0,
        isDraggable: !isSaving && !isInLinkDashboardFiltersMode, // There is a bug that happens if you are dragging an unsaved widget when the save dashboard function call returns. It creates a state that triggers errors rapidly. Disabling drag while saving avoids this error state.
        isResizable:
          isSaving || isInLinkDashboardFiltersMode
            ? false
            : SUPPORTED_WIDGETS_FOR_RESIZING.includes(viewType as WidgetTypes),
        minW: 1,
        maxW: DASHBOARD_COL_NUMBER,
        minH: isHeader ? 1 : widgetBaseRows,
        maxH: isHeader
          ? 1
          : isPowerBiIframe
            ? widgetBaseRows * 3
            : widgetBaseRows * 2,
        isBounded: true,
      };
    }
  );

  if (!isDragging && !isInLinkDashboardFiltersMode) {
    const baseLayoutForAddButton = getLayoutForNewWidget(widgets);
    const layoutForAddButton: Layout = {
      ...baseLayoutForAddButton,
      h: baseLayoutForAddButton.h * widgetBaseRows,
      y:
        baseLayoutForAddButton.y > minY
          ? getGridLayoutLibrarySpecificYValue(
              mappedYValues,
              baseLayoutForAddButton.y - 1
            )
          : 0,
      isDraggable: false,
      isResizable: false,
      i: WIDGET_ADD_BUTTON_ID,
    };
    breakpointAdjustedLayout.push(layoutForAddButton);
  }
  return breakpointAdjustedLayout;
};

const resolveLayoutCollision = (arr: Array<number>) => {
  let result: Array<number> = [];

  for (let i = 0; i < arr.length; i++) {
    const curr = arr[i];
    result.push(curr);
    if (curr === arr[i + 1]) {
      result = result.concat(
        resolveLayoutCollision(arr.slice(i + 1).map(i => i + 1))
      );
      break;
    }
  }
  return result;
};

const convertToBaseLayout = (
  newLayout: Layout[],
  currentBreakpoint: BreakpointKeys
) => {
  const currentBreakpointIndex = breakpointKeyList.indexOf(currentBreakpoint);
  const widgetHeight = getWidgetRows(currentBreakpointIndex);
  const sortedYs = sortedUniq(
    newLayout.sort((a, b) => a.y - b.y).map(({ y }) => y)
  );
  const normalizedRange = sortedYs.reduce<Record<number, number>>(
    (normalizedRange, y) => {
      normalizedRange[y] = Math.ceil(y / widgetHeight);
      return normalizedRange;
    },
    {}
  );
  const normalizedLayout = resolveLayoutCollision(
    Object.values(normalizedRange)
  );
  const normalizedRangeWithoutCollisions = Object.keys(normalizedRange).reduce<
    Record<string, number>
  >((previousValue, currentValue, currentIndex) => {
    previousValue[currentValue] = normalizedLayout[currentIndex];
    return previousValue;
  }, {});

  return newLayout.map(({ y, h, ...layout }) => ({
    ...layout,
    y: normalizedRangeWithoutCollisions[y],
    h: h === 1 ? h : Math.max(1, Math.round(h / widgetHeight)),
  }));
};

export const handleLayoutChange = (
  newLayout: Layout[],
  currentBreakpoint: BreakpointKeys,
  changeLayout: (newLayout: Layout[]) => void
) =>
  changeLayout(
    convertToBaseLayout(
      newLayout.filter(layout => layout.i !== WIDGET_ADD_BUTTON_ID),
      currentBreakpoint
    )
  );

export const getColSpanForViewType = (viewType?: WidgetTypes) =>
  viewType === WidgetTypes.HEADER
    ? HEADER_WIDGET_COL_SPAN
    : viewType === WidgetTypes.BUBBLE_CHART
      ? BUBBLE_CHART_WIDGET_COL_SPAN
      : DEFAULT_WIDGET_COL_SPAN;

export const getLayoutForNewWidget = (
  widgets: DashboardBuilderWidget[],
  viewType?: WidgetTypes
): Omit<Layout, 'i'> => {
  const commonLayout = {
    h: 1,
    w: getColSpanForViewType(viewType),
  };
  if (!widgets.length) {
    // there are no widgets
    return {
      x: 0,
      y: 0,
      ...commonLayout,
    };
  }
  const nextRowY = Math.max(
    ...widgets.map(({ layout }) => layout.y + layout.h)
  );
  if (
    viewType === WidgetTypes.HEADER ||
    viewType === WidgetTypes.BUBBLE_CHART
  ) {
    return { ...commonLayout, x: 0, y: nextRowY };
  }
  const lastRowWidgets = widgets.filter(
    ({ layout }) => layout.y + layout.h === nextRowY
  );
  const lastWidget = lastRowWidgets.reduce(
    (furthest, next) =>
      next.layout.x + next.layout.w > furthest.layout.x + furthest.layout.w
        ? next
        : furthest,
    lastRowWidgets[0] ?? widgets[0]
  );
  const columnAfterLastWidget = lastWidget.layout.x + lastWidget.layout.w;
  return {
    ...commonLayout,
    ...(columnAfterLastWidget < DASHBOARD_COL_NUMBER
      ? { x: columnAfterLastWidget, y: lastWidget.layout.y }
      : { x: 0, y: nextRowY }),
  };
};
